import { Stack } from "@certn/cascadia";
import { Card } from "@certn/cascadia-react";
import { css } from "@emotion/css";

import { PackageActions } from "./PackageActions";
import { PackageCardSkeleton } from "./PackageCardSkeleton";
import { PackageChecks } from "./PackageChecks";
import { PackageDescription } from "./PackageDescription";
import { PackageHeader } from "./PackageHeader";
import { useClientAccount } from "../../hooks/useClientAccount";
import { usePackageApplyLink } from "../../hooks/usePackageApplyLink";
import { ApplyLinkCreatedModal } from "../../pages/apply-links/components/ApplyLinkCreatedModal";
import { Schemas } from "../../types";
import { TagList } from "../tag-list/TagList";

const CAPACITY = 3;
interface Props {
  title: string;
  checks: string[];
  description: string;
  tags?: string[];
  testID: string;
  isDisabled: boolean;
  selectedGroup?: string | null;
  defaultApplyLink: Schemas["PackageDefaultApplyLink"] | null;
  packageId: string;
  onPress: () => void;
  mutate: () => void;
  canApplyLinkBeCreated?: boolean;
}

export function PackageCard({
  title,
  checks,
  description,
  tags = [],
  testID,
  isDisabled = false,
  selectedGroup,
  defaultApplyLink,
  packageId,
  onPress,
  mutate,
  canApplyLinkBeCreated,
}: Props) {
  const { customerGroups } = useClientAccount();
  const customerGroup = customerGroups?.find(
    (group) => group.id === selectedGroup
  );

  const { applyLinkData, createApplyLink, isApplyLinkLoading } =
    usePackageApplyLink(packageId, defaultApplyLink?.id, mutate);

  if (isApplyLinkLoading) {
    return <PackageCardSkeleton />;
  }

  return (
    <Card className={styles.card} testID={testID}>
      <Stack className={styles.flex} gap="space-2">
        <Stack gap="space-2" justify="space-between" className={styles.header}>
          <PackageHeader
            title={title}
            isDisabled={isDisabled}
            testID={`${testID}-package-header`}
          />
          <PackageChecks checks={checks} testID={testID} />
          {description && (
            <PackageDescription
              description={description}
              testID={`${testID}-package-description`}
            />
          )}
        </Stack>
        {tags.length > 0 && (
          <Stack gap="space-2">
            <div style={{ paddingTop: "var(--space-2)" }}>
              <TagList
                tags={tags}
                capacity={CAPACITY}
                testID={`${testID}-package`}
              />
            </div>
          </Stack>
        )}
        <Stack orientation="horizontal" justify="space-between" align="end">
          <PackageActions
            customerGroup={customerGroup}
            defaultApplyLink={defaultApplyLink}
            applyLinkData={applyLinkData}
            onPress={onPress}
            onCreateApplyLink={createApplyLink}
            testID={`${testID}-package-actions`}
            canApplyLinkBeCreated={canApplyLinkBeCreated}
          />
        </Stack>
      </Stack>
      <ApplyLinkCreatedModal />
    </Card>
  );
}

const styles = {
  card: css({
    display: "flex",
    width: 365,
    minWidth: 260,
    maxWidth: 488,
    borderRadius: "var(--border-radius-3)",
    "&:hover": {
      boxShadow: "var(--shadow-card)",
    },
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
    padding: "var(--space-3) var(--space-4) var(--space-4)",
  }),
  flex: css({
    justifyContent: "space-between",
    flex: 1,
  }),
  header: css({
    paddingBottom: "var(--space-3)",
  }),
};
